import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PipelineResult, ExcludedArticle, PreviewArticle, SourceError } from '@new-signal/shared';
import { cn } from '../lib/utils';

function shortenUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function relativeTime(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t('preview.justNow');
  if (mins < 60) return t('preview.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('preview.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('preview.daysAgo', { count: days });
}

function ReasonBadge({ reason, aiScore }: { reason: string; aiScore?: number }) {
  const { t } = useTranslation('feed-builder');

  const styles: Record<string, { bg: string; text: string }> = {
    keyword_filter: { bg: 'bg-accent-blue-light', text: 'text-accent-blue' },
    ai_filter: { bg: 'bg-accent-purple-light', text: 'text-accent-purple' },
    duplicate: { bg: 'bg-brand-fill', text: 'text-brand-subtle' },
  };

  const style = styles[reason] ?? styles['duplicate'];
  let label = '';
  if (reason === 'keyword_filter') label = t('preview.badge.keywordFilter');
  else if (reason === 'ai_filter') label = `${t('preview.badge.aiFilter')} · ${aiScore?.toFixed(2) ?? ''}`;
  else if (reason === 'duplicate') label = t('preview.badge.duplicate');

  return (
    <span className={cn('inline-block rounded-xl px-2 py-0.5 text-xs font-medium', style.bg, style.text)}>
      {label}
    </span>
  );
}

function IncludedArticleCard({ article, t }: { article: PreviewArticle; t: (key: string, opts?: Record<string, unknown>) => string }) {
  return (
    <div className="mb-2 rounded-lg border border-brand-border p-3">
      <a
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[15px] font-semibold text-brand-text no-underline"
      >
        {article.title}
      </a>
      <div className="mt-1 text-[13px] text-brand-muted">
        {shortenUrl(article.source_url)}
        {article.published_at ? ` · ${relativeTime(article.published_at, t)}` : ''}
      </div>
      {article.excerpt && (
        <p className="mt-1.5 text-sm leading-normal text-brand-subtle">
          {article.excerpt.length >= 200 ? `${article.excerpt}…` : article.excerpt}
        </p>
      )}
    </div>
  );
}

function ExcludedArticleCard({ article, t }: { article: ExcludedArticle; t: (key: string, opts?: Record<string, unknown>) => string }) {
  return (
    <div className="mb-1.5 rounded-lg border border-brand-border bg-brand-canvas p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-brand-subtle">{article.title}</span>
        <ReasonBadge reason={article.reason} aiScore={article.ai_score} />
      </div>
      <div className="mt-0.5 text-xs text-brand-muted">
        {shortenUrl(article.source_url)}
        {article.published_at ? ` · ${relativeTime(article.published_at, t)}` : ''}
      </div>
    </div>
  );
}

interface FeedPreviewProps {
  sourceCount: number;
  onRunPreview: () => Promise<PipelineResult>;
  configChanged: boolean;
  onConfigChangeDismiss: () => void;
}

export function FeedPreview({
  sourceCount,
  onRunPreview,
  configChanged,
  onConfigChangeDismiss,
}: FeedPreviewProps) {
  const { t } = useTranslation('feed-builder');

  const [result, setResult] = useState<PipelineResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFiltered, setShowFiltered] = useState(false);

  const handleRun = async () => {
    if (sourceCount === 0) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setShowFiltered(false);
    onConfigChangeDismiss();

    try {
      const res = await onRunPreview();
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  if (sourceCount === 0) {
    return (
      <div className="py-4 text-center text-sm text-brand-muted">
        {t('preview.noSources')}
      </div>
    );
  }

  return (
    <div className="mt-4">
      {/* Preview button */}
      <div className="mb-3 text-center">
        <button
          onClick={handleRun}
          disabled={loading || sourceCount === 0}
          className={cn(
            'inline-flex min-h-11 items-center justify-center rounded-full border border-brand-text bg-white px-6 text-sm font-medium leading-6 tracking-[-0.01em] text-brand-text transition-colors hover:bg-brand-surface',
            loading || sourceCount === 0
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer',
          )}
        >
          {loading ? t('preview.loading') : t('preview.button')}
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="p-4 text-center text-brand-muted">
          <span className="mr-2 inline-block spin">⏳</span>
          {t('preview.fetchingMessage')}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-3 rounded-lg border border-accent-error-border bg-accent-error-bg p-3 text-sm text-accent-error-text">
          {error}
        </div>
      )}

      {/* Config changed notice */}
      {configChanged && result && !loading && (
        <div className="mb-3 rounded-lg border border-accent-warning-border bg-accent-warning-bg px-3.5 py-2.5 text-sm text-accent-warning-text">
          {t('preview.configChanged')}
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <div className="rounded-lg border border-brand-border-medium p-4">
          {/* Summary line */}
          <div className="mb-3 text-sm text-brand-subtle">
            {t('preview.summary', {
              included: result.total_included,
              total: result.total_fetched,
              excluded: result.total_excluded,
            })}
            {result.included.length < result.total_included && (
              <span className="ml-1 text-brand-muted">
                {t('preview.truncated', { count: result.included.length })}
              </span>
            )}
          </div>

          {/* Source errors */}
          {result.source_errors.length > 0 && (
            <div className="mb-3">
              {result.source_errors.map((err: SourceError, i: number) => (
                <div
                  key={i}
                  className="mb-1 rounded-md border border-accent-error-border bg-accent-error-bg p-2 text-[13px] text-accent-error-text"
                >
                  {t('preview.sourceError', { url: shortenUrl(err.source_url) })} — {err.error}
                </div>
              ))}
            </div>
          )}

          {result.summary_failures > 0 && (
            <div className="mb-3 rounded-md border border-accent-warning-border bg-accent-warning-bg p-2 text-[13px] text-accent-warning-text">
              {t('preview.summarizeFailures', { count: result.summary_failures })}
            </div>
          )}

          {result.translation_failures > 0 && (
            <div className="mb-3 rounded-md border border-accent-warning-border bg-accent-warning-bg p-2 text-[13px] text-accent-warning-text">
              {t('preview.translateFailures', { count: result.translation_failures })}
            </div>
          )}

          {/* Included articles */}
          {result.included.length > 0 ? (
            result.included.map((article: PreviewArticle, i: number) => (
              <IncludedArticleCard key={i} article={article} t={t} />
            ))
          ) : (
            <div className="p-4 text-center text-sm text-brand-muted">
              {result.total_fetched === 0
                ? t('preview.noArticles')
                : t('preview.allFiltered')}
            </div>
          )}

          {/* Excluded / filtered section */}
          {result.excluded.length > 0 && (
            <div className="mt-3">
              <button
                onClick={() => setShowFiltered((prev) => !prev)}
                className="cursor-pointer border-none bg-transparent px-0 py-1 text-sm font-medium text-brand-subtle"
              >
                {showFiltered ? '▼' : '▶'}{' '}
                {t('preview.showFiltered', { count: result.excluded.length })}
              </button>

              {showFiltered && (
                <div className="mt-2">
                  {result.excluded.map((article: ExcludedArticle, i: number) => (
                    <ExcludedArticleCard key={i} article={article} t={t} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
